import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiFilter,
  FiAlertTriangle,
  FiInbox,
  FiCheckCircle,
  FiXCircle,
  FiCalendar,
} from "react-icons/fi";
import request from "../../utils/request";
import SpinnerQ from "../../components/SpinnerQ/SpinnerQ";
import TablePaginationQ from "../../components/TableBionovaQ/TablePaginationQ/TablePaginationQ";
import df from "../../utils/dateFormater";
import parseFiltersDate from "../../utils/parseFiltersDate";
import { getRegionalFormat, getSession } from "../../utils/sessionHandler";
import PageContainer from "../../components/PageContainer/PageContainer";
import useTableQ from "../../utils/Hooks/useTableQ";
import styles from "./ReceptionStatus.module.css";
import t from "../../utils/translation";

const useDebouncedValue = (v, d = 400) => {
  const [deb, setDeb] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setDeb(v), d);
    return () => clearTimeout(id);
  }, [v, d]);
  return deb;
};

const chevronV = { closed: { rotate: 0 }, open: { rotate: 180 } };
const rowHoverV = {
  hover: { backgroundColor: "var(--red)" },
  tap: { scale: 0.98 },
};

/* ------------ Destinos ------------ */
const DESTS = [
  { key: "wash", label: "Wash", color: "blue" },
  { key: "ster", label: "Ster.", color: "green" },
  { key: "broken", label: "Broken", color: "brown" },
  { key: "lost", label: "Lost", color: "red" },
];

export default function ReceptionStatus() {
  const [receptions, setReceptions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [operators, setOperators] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const [openPkgs, setOpenPkgs] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [openDisp, setOpenDisp] = useState({});
  const toggleDisp = (id) => setOpenDisp((p) => ({ ...p, [id]: !p[id] }));

  /* ----- filtros ----- */
  const [filters, setFilters] = useState({
    receptionNumber: "",
    dispatchNumber: "",
    dateFrom: "",
    dateTo: "",
    isUrgent: "",
    areaId: "",
    userName: "",
  });
  const F = useDebouncedValue(filters);

  /* ----- paginación ----- */
  const {
    currentPage,
    currentItems: currentReceptions,
    itemsPerPage,
    handleChangePage,
    handleChangeItemsPerPage,
    setCurrentPage,
  } = useTableQ(
    receptions,
    ["receptionNumber", "responsible", "creationDate"],
    totalPages
  );

  /* ----- datos estáticos (áreas, usuarios) ----- */
  useEffect(() => {
    (async () => {
      try {
        const [aRes, opRes] = await Promise.all([
          request().get(`/api/area?companyId=${getSession().Company.Id}`),
          request().get(
            `/api/appuser/getByCompany?companyId=${getSession().Company.Id}`
          ),
        ]);
        setAreas(aRes.data || []);
        setOperators(opRes.data || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  /* ----- obtener recepciones ----- */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p = new URLSearchParams();
        p.append("page", currentPage);
        p.append("pageSize", itemsPerPage);

        if (F.receptionNumber) p.append("receptionNumber", F.receptionNumber);
        if (F.dispatchNumber) p.append("dispatchNumber", F.dispatchNumber);
        if (F.isUrgent) p.append("isUrgent", F.isUrgent);
        if (F.areaId) p.append("areaId", F.areaId);
        if (F.userName) p.append("responsibleId", F.userName);

        if (F.dateFrom)
          p.append(
            "dateFrom",
            parseFiltersDate(
              df(F.dateFrom, "date"),
              getRegionalFormat().dateFormat
            )
          );
        if (F.dateTo)
          p.append(
            "dateTo",
            parseFiltersDate(
              df(F.dateTo, "date"),
              getRegionalFormat().dateFormat,
              true
            )
          );

        const { data } = await request().get(`/api/reception/list?${p}`);
        setReceptions(data.receptions || data);
        setTotalPages(data.totalPages);
      } catch (err) {
        console.error(err);
        // setReceptions([]);
        setReceptions([
          {
            receptionNumber: 2001,
            responsible: "Juan Pérez",
            creationDate: "2025-05-09T09:10:00Z",
            receptionStatus: 2, // 1=pending, 2=confirmed
            areaName: "Ortopedia",
            isUrgent: false,
            dispatches: [
              {
                dispatchId: 1050,
                dispatchNumber: 1050,
                dispatchStatus: 2, // 1=dispatched, 2=received
                packages: [
                  {
                    packageId: 300,
                    packageNumber: "PK-300",
                    packageName: "Bandeja Osteosíntesis",
                    materials: [
                      {
                        materialId: 501,
                        materialName: "Tornillo 3,5 ×14 mm",
                        serial: "SRL-501-A1",
                        quantity: 1,
                        quantities: { wash: 1, ster: 0, broken: 0, lost: 0 },
                      },
                      {
                        materialId: 502,
                        materialName: "Placa LCP 7 aguj.",
                        serial: "SRL-502-B7",
                        quantity: 1,
                        quantities: { wash: 0, ster: 1, broken: 0, lost: 0 },
                      },
                    ],
                  },
                ],
              },
            ],
          },

          {
            receptionNumber: 2002,
            responsible: "Ana Rodríguez",
            creationDate: "2025-05-08T15:45:00Z",
            receptionStatus: 1,
            areaName: "Esterilización",
            isUrgent: true,
            dispatches: [
              {
                dispatchId: 1055,
                dispatchNumber: 1055,
                dispatchStatus: 1,
                packages: [
                  {
                    packageId: 410,
                    packageNumber: "PK-410",
                    packageName: "Cánulas Artroscopia",
                    materials: [
                      {
                        materialId: 600,
                        materialName: "Cánula 5 mm",
                        serial: "ART-600-C1",
                        quantity: 2,
                        quantities: { wash: 2, ster: 0, broken: 0, lost: 0 },
                      },
                      {
                        materialId: 601,
                        materialName: "Obturador 5 mm",
                        serial: "ART-601-O9",
                        quantity: 2,
                        quantities: { wash: 1, ster: 0, broken: 1, lost: 0 },
                      },
                      {
                        materialId: 602,
                        materialName: "Trocar 5 mm",
                        serial: "ART-602-T5",
                        quantity: 2,
                        quantities: { wash: 0, ster: 0, broken: 0, lost: 2 },
                      },
                    ],
                  },
                ],
              },
              {
                dispatchId: 1056,
                dispatchNumber: 1056,
                dispatchStatus: 1,
                packages: [
                  {
                    packageId: 411,
                    packageNumber: "PK-411",
                    packageName: "Kit Suturas",
                    materials: [
                      {
                        materialId: 700,
                        materialName: "Porta aguja Mayo-Hegar",
                        serial: null,
                        quantity: 1,
                        quantities: { wash: 0, ster: 1, broken: 0, lost: 0 },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ]);
      } finally {
        setLoading(false);
      }
    })();
  }, [F, currentPage, itemsPerPage]);

  /* ----- toggles filas ----- */
  const toggleRow = (id) => setOpenRows((p) => ({ ...p, [id]: !p[id] }));
  const togglePkg = (id) => setOpenPkgs((p) => ({ ...p, [id]: !p[id] }));

  /* ----- filtros onChange ----- */
  const onFilter = (e) => {
    const name = e.target.getAttribute("name");
    const value = e.target.value;
    if (!name) return;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ----- badges estado recepción ----- */
  const renderReceptionStatus = (st) => {
    if (st === 1)
      return (
        <span className={`${styles.badge} ${styles.badgeOrange}`}>
          <FiInbox size={12} /> Pending
        </span>
      );
    if (st === 2)
      return (
        <span className={`${styles.badge} ${styles.badgeGreen}`}>
          <FiCheckCircle size={12} /> Confirmed
        </span>
      );
    return null;
  };

  /* ----- status paquete (basado en materiales) ----- */
  const renderPackageStatus = (pkg) => {
    const allOk = pkg.materials.every((m) =>
      DESTS.every((d) => (m.quantities[d.key] || 0) === 0)
    );
    if (allOk)
      return (
        <span className={`${styles.badge} ${styles.badgeOrange}`}>
          <FiInbox size={11} /> Pending
        </span>
      );
    return (
      <span className={`${styles.badge} ${styles.badgeGreen}`}>
        <FiCheckCircle size={11} /> Assigned
      </span>
    );
  };

  return (
    <PageContainer
      categoryId={5}
      category="Reception"
      backUrl="/appreceptionmenu"
      title="Reception Status"
    >
      <div className={styles.screenWrapper}>
        {/* Filtros */}
        <section className={styles.filtersBar}>
          <FiFilter className={styles.filterIcon} />
          <input
            name="receptionNumber"
            placeholder="Reception #"
            value={filters.receptionNumber}
            onChange={onFilter}
          />
          <input
            name="dispatchNumber"
            placeholder="Dispatch #"
            value={filters.dispatchNumber}
            onChange={onFilter}
          />
          <select name="isUrgent" value={filters.isUrgent} onChange={onFilter}>
            <option value="">Urgency (all)</option>
            <option value="true">Urgent</option>
            <option value="false">Scheduled</option>
          </select>
          <select name="areaId" value={filters.areaId} onChange={onFilter}>
            <option value="">All areas</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select name="userName" value={filters.userName} onChange={onFilter}>
            <option value="">All responsibles</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>
                {op.toString}
              </option>
            ))}
          </select>
        </section>

        {/* Tabla */}
        <div className={styles.tableWrapper}>
          <div className={styles.tableHeader}>
            <span />
            <span>Reception #</span>
            <span>Responsible</span>
            <span>Date</span>
            <span>Status</span>
            <span>Area</span>
            <span>Dispatches</span>
            <span>Urgency</span>
          </div>

          {(loading || currentReceptions.length === 0) && (
            <div className="spinnerContainer">
              {loading ? (
                <SpinnerQ category="reception" />
              ) : (
                <p>{t("NoData") || "No data loaded."}</p>
              )}
            </div>
          )}

          {!loading && currentReceptions.length > 0 && (
            <AnimatePresence initial={false}>
              {currentReceptions.map((r) => {
                const open = !!openRows[r.receptionNumber];
                return (
                  <motion.div key={r.receptionNumber} layout>
                    <motion.button
                      className={styles.row}
                      onClick={() => toggleRow(r.receptionNumber)}
                      variants={rowHoverV}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <motion.span
                        variants={chevronV}
                        animate={open ? "open" : "closed"}
                        className={styles.chevron}
                      >
                        {open ? <FiChevronUp /> : <FiChevronDown />}
                      </motion.span>

                      <span className={styles.receptionNumber}>
                        {r.receptionNumber}
                      </span>
                      <span>{r.responsible}</span>
                      <span>{df(r.creationDate, "dateTime")}</span>
                      <span>{renderReceptionStatus(r.receptionStatus)}</span>
                      <span>{r.areaName}</span>
                      <span>{r.dispatches.length}</span>
                      <span>
                        {r.isUrgent ? (
                          <span
                            className={`${styles.badge} ${styles.badgeOrange}`}
                          >
                            <FiAlertTriangle size={12} /> Urgent
                          </span>
                        ) : (
                          <span
                            className={`${styles.badge} ${styles.badgeScheduled}`}
                          >
                            <FiCalendar size={12} /> Scheduled
                          </span>
                        )}
                      </span>
                    </motion.button>

                    {/* ---------- Despachos ---------- */}
                    <AnimatePresence>
                      {open && (
                        <motion.div layout className={styles.dispatchesWrapper}>
                          {r.dispatches.map((d) => {
                            const dOpen = !!openDisp[d.dispatchId];
                            return (
                              <motion.div key={d.dispatchId} layout>
                                {/* Fila DESPACHO */}
                                <button
                                  className={styles.dispatchRow}
                                  onClick={() => toggleDisp(d.dispatchId)}
                                >
                                  <motion.span
                                    variants={chevronV}
                                    animate={dOpen ? "open" : "closed"}
                                    className={styles.chevronPkg}
                                  >
                                    {dOpen ? (
                                      <FiChevronUp />
                                    ) : (
                                      <FiChevronDown />
                                    )}
                                  </motion.span>
                                  <FiPackage className={styles.pkgIcon} />
                                  <span className={styles.pkgNumber}>
                                    {d.dispatchNumber}
                                  </span>
                                  {renderReceptionStatus(d.dispatchStatus)}
                                  <span className={styles.pkgName}>
                                    {d.packages.length} packages
                                  </span>
                                </button>

                                {/* ---------- Paquetes dentro del despacho ---------- */}
                                <AnimatePresence>
                                  {dOpen &&
                                    d.packages.map((pkg) => {
                                      const pOpen = !!openPkgs[pkg.packageId];
                                      return (
                                        <motion.div key={pkg.packageId} layout>
                                          {/* fila PAQUETE */}
                                          <button
                                            className={styles.packageRow}
                                            onClick={() =>
                                              togglePkg(pkg.packageId)
                                            }
                                          >
                                            <motion.span
                                              variants={chevronV}
                                              animate={
                                                pOpen ? "open" : "closed"
                                              }
                                              className={styles.chevronPkg}
                                            >
                                              {pOpen ? (
                                                <FiChevronUp />
                                              ) : (
                                                <FiChevronDown />
                                              )}
                                            </motion.span>
                                            <FiPackage
                                              className={styles.pkgIcon}
                                            />
                                            <span className={styles.pkgNumber}>
                                              {pkg.packageNumber}
                                            </span>
                                            {renderPackageStatus(pkg)}
                                            <span className={styles.pkgName}>
                                              {pkg.packageName}
                                            </span>
                                            <span
                                              className={styles.materialCount}
                                            >
                                              {pkg.materials.length} materials
                                            </span>
                                          </button>

                                          {/* tabla de MATERIALES */}
                                          <AnimatePresence>
                                            {pOpen && (
                                              <motion.table
                                                layout
                                                className={styles.materialTable}
                                              >
                                                <thead>
                                                  <tr>
                                                    <th>Material</th>
                                                    <th>Serial</th>
                                                    <th>Qty</th>
                                                    {DESTS.map((d) => (
                                                      <th key={d.key}>
                                                        {d.label}
                                                      </th>
                                                    ))}
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {pkg.materials.map((m) => (
                                                    <tr
                                                      key={`${m.materialId}-${
                                                        m.serial || "_"
                                                      }`}
                                                    >
                                                      <td>{m.materialName}</td>
                                                      <td>{m.serial || "—"}</td>
                                                      <td>{m.quantity}</td>
                                                      {DESTS.map((dest) => (
                                                        <td
                                                          key={dest.key}
                                                          className={
                                                            styles.centered
                                                          }
                                                        >
                                                          {m.quantities?.[
                                                            dest.key
                                                          ] ?? 0}
                                                        </td>
                                                      ))}
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </motion.table>
                                            )}
                                          </AnimatePresence>
                                        </motion.div>
                                      );
                                    })}
                                </AnimatePresence>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          <TablePaginationQ
            currentPage={currentPage}
            totalPages={totalPages}
            handleChangePage={handleChangePage}
            handleChangeItemsPerPage={handleChangeItemsPerPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </PageContainer>
  );
}
